import type { SequenceRead } from './sequenceHead.ts'
import { bestPrefixEditDistance, readMask, toMasks } from './iupac.ts'
import type { PrimerPair, PrimerStartRates, PrimerTrimOptions } from './primerTrimDetection.ts'
import { DEFAULT_PRIMER_TRIM_OPTIONS, primerStartRates } from './primerTrimDetection.ts'
import type { KnownPrimerPair } from './primerRegistry.ts'
import { stripAdapter } from './primerRegistry.ts'
import { PAIR_FOUND_AT_LEAST, identifyPrimerPair, searchPrimerPair } from './primerPairIdentification.ts'

/**
 * What one sample's reads say about the primers entered for it.
 *
 * - `PRIMERS_PRESENT`: the entered primers open the reads — an untrimmed configuration fits.
 * - `PRIMERS_ABSENT`: no known primer opens the reads — they were trimmed before upload.
 * - `OTHER_PRIMER`: a different registered primer opens the reads — wrong primer or wrong target.
 * - `ONE_PRIMER_MISSING`: one mate opens with its primer, the other with neither — one entered primer is wrong.
 * - `TAG_MISMATCH`: the gene primer is there, but the sample-specific 5′ tag entered with it is not the
 *   tag on the reads — primers were assigned to the wrong samples, or the files hold several tagged samples.
 */
export type PrimerDiagnosis = 'PRIMERS_PRESENT' | 'PRIMERS_ABSENT' | 'OTHER_PRIMER' | 'ONE_PRIMER_MISSING' | 'TAG_MISMATCH' | 'UNKNOWN'

export type PrimerDiagnosisResult = {
  diagnosis: PrimerDiagnosis
  reason: string
  perFile: PrimerStartRates[]
  /** Share of tag-bearing reads whose tag differs from the entered one; `null` when the primers carry no tag or the reads show none. */
  tagMismatchRate: number | null
  /** The known pair that does open the reads, for `OTHER_PRIMER` / `ONE_PRIMER_MISSING`. */
  suggestedPair?: KnownPrimerPair
}

const MIN_TAG_BASES = 4
const MIN_TAG_READS = 20
const TAG_MISMATCH_AT_LEAST = 0.5
// A swapped primer shows one other tag; a negative control's bled-over reads spread over many samples' tags.
const OTHER_TAG_DOMINANT_AT_LEAST = 0.4

type TaggedPrimer = { core: number[]; coreLength: number; maxErrors: number; extra: number[] }

const taggedPrimer = (primer: string, options: PrimerTrimOptions): TaggedPrimer => {
  const clean = stripAdapter(primer)
  const coreLength = Math.min(options.coreLength, clean.length)
  return {
    core: toMasks(clean.slice(-coreLength)),
    coreLength,
    maxErrors: Math.floor(coreLength * options.maxErrorRate),
    extra: toMasks(clean.slice(0, clean.length - coreLength))
  }
}

/** Where `primer`'s core best aligns in the read's first bases, with its edit distance. */
const bestStart = (read: string, primer: TaggedPrimer, window: number): { start: number; distance: number } => {
  /* the best-scoring start, not the first passing one: an indel-tolerant match also passes a base or two early, which would misalign the tag */
  let start = -1
  let distance = primer.maxErrors + 1
  for (let s = 0; s <= window && s + primer.coreLength <= read.length; s++) {
    const d = bestPrefixEditDistance(primer.core, read.slice(s), 0, primer.maxErrors)
    if (d < distance) {
      distance = d
      start = s
    }
  }
  return { start, distance }
}

/**
 * The bases a customer primer carries 5′ of its matched core, compared right-aligned against the bases
 * the read carries before the core. Each read is compared with whichever of the two primers opens it,
 * so reads in both orientations are fine. Only reads with at least {@link MIN_TAG_BASES} such bases count,
 * so primers written with a full Illumina adapter (which the read never contains) produce no evidence.
 */
const tagMismatchRate = (
  reads: readonly SequenceRead[],
  primers: PrimerPair,
  options: PrimerTrimOptions
): { rate: number; reads: number; topOtherTag: number } => {
  const candidates = [taggedPrimer(primers.forward, options), taggedPrimer(primers.reverse, options)]
  if (candidates.every((p) => p.extra.length < MIN_TAG_BASES)) {
    return { rate: 0, reads: 0, topOtherTag: 0 }
  }
  let judged = 0
  let mismatched = 0
  const otherTags = new Map<string, number>()
  for (const read of reads) {
    const found = candidates
      .map((primer) => ({ primer, ...bestStart(read.sequence, primer, primer.extra.length + options.maxOffset) }))
      .filter((m) => m.start >= 0)
      .sort((a, b) => a.distance - b.distance)[0]
    if (!found) {
      continue
    }
    const k = Math.min(found.start, found.primer.extra.length)
    if (k < MIN_TAG_BASES) {
      continue
    }
    judged++
    let mismatches = 0
    for (let i = 1; i <= k; i++) {
      const readBase = readMask(read.sequence[found.start - i])
      if ((readBase & found.primer.extra[found.primer.extra.length - i]) === 0) {
        mismatches++
      }
    }
    if (mismatches > Math.max(1, Math.floor(k * 0.15))) {
      mismatched++
      const tag = read.sequence.slice(found.start - k, found.start)
      otherTags.set(tag, (otherTags.get(tag) ?? 0) + 1)
    }
  }
  return {
    rate: judged === 0 ? 0 : mismatched / judged,
    reads: judged,
    topOtherTag: judged === 0 ? 0 : Math.max(0, ...otherTags.values()) / judged
  }
}

const mean = (values: number[]): number => values.reduce((a, b) => a + b, 0) / Math.max(1, values.length)

/**
 * Checks one sample's reads (one file when merged or single-ended, R1 and R2 when paired) against the
 * primers entered for it and, when given, against every registered primer. Not for multiplexed
 * (`COMBINED_SAMPLE_PAIRS`) files, which carry every sample's tag by design.
 */
export const diagnoseSamplePrimers = (
  files: readonly (readonly SequenceRead[])[],
  primers: PrimerPair,
  registry: readonly KnownPrimerPair[] = [],
  givenOptions: PrimerTrimOptions = DEFAULT_PRIMER_TRIM_OPTIONS
): PrimerDiagnosisResult => {
  /* mates always open with their primer; their 3′ read-through is not evidence (see the classifier) */
  const options: PrimerTrimOptions = files.length > 1 ? { ...givenOptions, evidence: 'start' } : givenOptions
  const rates = files.map((reads) => primerStartRates(reads, primers, options))
  if (rates.some((r) => r === null)) {
    return { diagnosis: 'UNKNOWN', reason: 'no usable primer sequence', perFile: [], tagMismatchRate: null }
  }
  const perFile = rates as PrimerStartRates[]
  if (perFile.some((r) => r.readsExamined < options.minReads)) {
    return { diagnosis: 'UNKNOWN', reason: 'too few reads to judge', perFile, tagMismatchRate: null }
  }
  const found = mean(perFile.map((r) => r.primerFoundRate))

  if (found <= options.trimmedAtMost) {
    /* judged on the sample's reads together: an R2 file alone cannot tell apart pairs sharing its primer */
    const { match: other, oneSided } = searchPrimerPair(files.flat(), registry, [], options)
    if (other) {
      return {
        diagnosis: 'OTHER_PRIMER',
        reason: `reads carry the known ${other.pair.amplicons.join('/')} pair, not the entered one`,
        perFile,
        tagMismatchRate: null,
        suggestedPair: other.pair
      }
    }
    if (oneSided) {
      return {
        diagnosis: 'OTHER_PRIMER',
        reason: `reads carry a known ${[...new Set(oneSided.flatMap((pair) => pair.amplicons))].join('/')} primer, not the entered ones`,
        perFile,
        tagMismatchRate: null
      }
    }
    return { diagnosis: 'PRIMERS_ABSENT', reason: 'no known primer opens the reads', perFile, tagMismatchRate: null }
  }

  if (perFile.length === 2) {
    const [a, b] = perFile
    const oneSided = (x: PrimerStartRates, y: PrimerStartRates) =>
      x.primerFoundRate >= options.untrimmedAtLeast && y.primerFoundRate <= options.trimmedAtMost
    if (oneSided(a, b) || oneSided(b, a)) {
      const missingIndex = oneSided(a, b) ? 1 : 0
      const other = identifyPrimerPair(files[missingIndex], registry, [], options)
      return {
        diagnosis: 'ONE_PRIMER_MISSING',
        reason: `the ${missingIndex === 0 ? 'first' : 'second'} file carries neither entered primer`,
        perFile,
        tagMismatchRate: null,
        suggestedPair: other && other.rate >= PAIR_FOUND_AT_LEAST ? other.pair : undefined
      }
    }
  }

  if (found < options.untrimmedAtLeast) {
    return { diagnosis: 'UNKNOWN', reason: 'primers open only some reads', perFile, tagMismatchRate: null }
  }

  const tags = files.map((reads) => tagMismatchRate(reads, primers, options))
  const tagReads = tags.reduce((n, t) => n + t.reads, 0)
  const tagRate = tagReads === 0 ? null : tags.reduce((n, t) => n + t.rate * t.reads, 0) / tagReads
  const topOtherTag = tagReads === 0 ? 0 : tags.reduce((n, t) => n + t.topOtherTag * t.reads, 0) / tagReads
  if (
    tagRate !== null &&
    tagReads >= MIN_TAG_READS &&
    tagRate >= TAG_MISMATCH_AT_LEAST &&
    topOtherTag >= OTHER_TAG_DOMINANT_AT_LEAST
  ) {
    return {
      diagnosis: 'TAG_MISMATCH',
      reason: 'the gene primer opens the reads, but the tag in front of it is not the one entered for this sample',
      perFile,
      tagMismatchRate: tagRate
    }
  }
  return { diagnosis: 'PRIMERS_PRESENT', reason: 'the entered primers open the reads', perFile, tagMismatchRate: tagRate }
}
